import { useState } from "react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import {
  ArtifactRefreshRun,
  isRunLive,
  projectPinnedArtifactsPath,
  refreshPinnedArtifact,
  refreshRunPath,
  pinnedArtifactPreviewPath,
} from "~/services/pinnedArtifacts";

const POLL_INTERVAL_MS = 2000;

export interface UseArtifactRefreshRun {
  run: ArtifactRefreshRun | undefined;
  isStarting: boolean;
  start: () => Promise<void>;
}

/** Starts a refresh and follows it to a terminal status.
 *
 * The run id is held here rather than read from the pin, so a run this tab
 * started is followed even before the pin has anything to say about it. On a
 * reload the pin's `last_refresh_run_id` seeds it, which is what makes a run
 * started elsewhere — or before a refresh — visible too.
 *
 * Polling stops as soon as the run reaches a terminal status: SWR takes a
 * function for `refreshInterval` and treats 0 as "stop", so the run's own state
 * ends the loop without an effect watching it.
 */
export const useArtifactRefreshRun = (
  projectId: string,
  pinId: string,
  seedRunId: string | null
): UseArtifactRefreshRun => {
  const { showError } = useToast();
  const [runId, setRunId] = useState<string | null>(seedRunId);
  const [isStarting, setIsStarting] = useState(false);

  const { data: run } = useFetcher<ArtifactRefreshRun>(
    runId ? refreshRunPath(projectId, pinId, runId) : null,
    {
      refreshInterval: (latest) => (isRunLive(latest) ? POLL_INTERVAL_MS : 0),
      onSuccess: (latest) => {
        if (latest.status !== "succeeded") return;
        // The pin now points at a newly produced artifact, so both the list and
        // this card's preview are stale. Revalidating the preview is what makes
        // the card show the refreshed result without a reload.
        void mutate(projectPinnedArtifactsPath(projectId));
        void mutate(pinnedArtifactPreviewPath(projectId, pinId));
      },
    }
  );

  const start = async () => {
    setIsStarting(true);
    try {
      const started = await refreshPinnedArtifact(projectId, pinId);
      setRunId(started.id);
    } catch (e) {
      // A workflow that cannot be re-run at all answers 422 with the reason,
      // which is more useful to show than a generic failure.
      showError(e instanceof Error && e.message ? e.message : "Couldn't refresh this artifact");
    } finally {
      setIsStarting(false);
    }
  };

  return { run, isStarting, start };
};
